import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const Ctx = createContext(null);
export const useExtraction = () => useContext(Ctx);

// Tiene vive le letture AI dei documenti anche cambiando pagina: i job girano sul server,
// qui si segue l'avanzamento e si avvisa quando il risultato è pronto.
export function ExtractionProvider({ children }) {
  const [jobs, setJobs] = useState([]);
  const jobsRef = useRef([]);
  const watched = useRef({});
  jobsRef.current = jobs;

  const start = useCallback(async ({ url, payload, files = [], label = "" }) => {
    const { data } = await api.post(url, payload);
    setJobs((prev) => [...prev, {
      id: data.job_id, totale: data.totale || files.length, fatti: 0,
      stato: "in_corso", files, label, result: null, messaggio: "",
    }]);
    return data.job_id;
  }, []);

  const claim = useCallback((id) => {
    delete watched.current[id];
    setJobs((prev) => prev.filter((j) => j.id !== id));
  }, []);
  const watch = useCallback((id, on) => { watched.current[id] = on; }, []);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("rb_extract_jobs") || "[]");
      if (saved.length) setJobs(saved.map((s) => ({ ...s, files: [], fatti: 0, stato: "in_corso", result: null, messaggio: "" })));
    } catch (e) {
      console.warn("Letture in corso non ripristinabili:", e);
    }
  }, []);

  useEffect(() => {
    const attivi = jobs.filter((j) => j.stato === "in_corso").map((j) => ({ id: j.id, totale: j.totale, label: j.label }));
    localStorage.setItem("rb_extract_jobs", JSON.stringify(attivi));
  }, [jobs]);

  useEffect(() => {
    const t = setInterval(async () => {
      const running = jobsRef.current.filter((j) => j.stato === "in_corso");
      for (const j of running) {
        try {
          const { data } = await api.get(`/extract-jobs/${j.id}`);
          setJobs((prev) => prev.map((x) => {
            if (x.id !== j.id) return x;
            const avanzato = (data.fatti || 0) !== x.fatti;
            const fermoDa = avanzato ? Date.now() : (x.fermoDal || Date.now());
            const bloccato = data.stato === "in_corso" && Date.now() - fermoDa > 5 * 60 * 1000;
            return {
              ...x, fatti: data.fatti || 0, totale: data.totale || x.totale, errori: 0, fermoDal: fermoDa,
              stato: bloccato ? "errore" : data.stato,
              messaggio: bloccato ? "Il motore di lettura non risponde. Riprova o esegui la diagnostica in Impostazioni." : (data.messaggio || ""),
              result: data.stato === "completo" ? data : null,
            };
          }));
          if (data.stato !== "in_corso" && !watched.current[j.id]) {
            if (data.stato === "completo") {
              toast.success(`Lettura completata: ${data.analizzati} documento/i pronti. Riapri la scheda per applicare i dati.`, { duration: 10000 });
            } else {
              toast.error(data.messaggio || "Estrazione documenti non riuscita.");
            }
          }
        } catch (e) {
          const stato = e?.response?.status;
          const errori = (j.errori || 0) + 1;
          if (stato === 404 || errori >= 10) {
            const msg = stato === 404 ? "Elaborazione non trovata (il server è ripartito)."
              : `Il server non risponde alla verifica della lettura${stato ? ` (errore ${stato})` : ""}.`;
            setJobs((prev) => prev.map((x) => x.id === j.id ? { ...x, stato: "errore", messaggio: msg } : x));
            if (!watched.current[j.id]) toast.error(msg);
          } else {
            setJobs((prev) => prev.map((x) => x.id === j.id ? { ...x, errori } : x));
          }
        }
      }
    }, 2000);
    return () => clearInterval(t);
  }, []);

  return <Ctx.Provider value={{ jobs, start, claim, watch }}>{children}</Ctx.Provider>;
}
